import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useState } from "react";
import { ReactComponent as WorkflowIcon } from "../../../assets/icons/workflow-2.svg";
import { scrollbarStyle } from "../../../shared/dashboard";
import WorkflowProgressStepper, { WorkflowStepConfig } from "../../atoms/workflows/WorkflowProgressStepper";
import EmailType from "../../molecules/home-manage-fields/EmailType";
import Enrichment from "../../molecules/home-manage-fields/Enrichment";
import NewEmail from "../../molecules/home-manage-fields/NewEmail";
import Sync from "../../molecules/home-manage-fields/Sync";

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 'calc(100vw - 48px)',
            maxWidth: 750,
            height: 'calc(100vh - 48px)',
            maxHeight: 800,
            borderRadius: 24,
            [theme.breakpoints.down('md')]: {
                width: 'calc(100vw - 32px)',
                height: 'calc(100vh - 32px)',
                maxHeight: 'none',
                borderRadius: 16,
            },
            [theme.breakpoints.down('sm')]: {
                width: '100vw',
                height: '100vh',
                maxHeight: 'none',
                borderRadius: 0,
                margin: 0,
            },
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#fff',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.75rem',
        background: 'linear-gradient(135deg, #1eb6a8 0%, #18a598 100%)',
        padding: '32px 40px',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            padding: '24px 20px',
        },
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0',
        flex: 1,
        overflow: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px 40px',
        background: '#f8f9fc',
        [theme.breakpoints.down('sm')]: {
            padding: '16px 20px',
        },
    },
    headerIcon: {
        width: 64,
        height: 64,
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 16,
        fontSize: 32,
    },
    closeIcon: {
        position: 'absolute',
        top: '24px',
        right: '24px',
        width: 40,
        height: 40,
        padding: 'unset',
        borderRadius: 8,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        "& > .MuiSvgIcon-root": {
            fill: '#fff'
        },
    },
    backBtn: {
        height: 38,
        minWidth: 110,
        background: 'white',
        color: '#64748b',
        border: '2px solid #e2e8f0',
        borderRadius: 10,
        textTransform: 'none',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: '600',
        transition: 'ease-in-out 300ms',
    },
    nextBtn: {
        height: 38,
        minWidth: 110,
        borderRadius: 10,
        background: 'linear-gradient(135deg, #1eb6a8 0%, #18a598 100%)',
        color: 'white',
        textTransform: 'none',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: '600',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
    manageFolderBtn: {
        height: 45,
        background: 'white',
        color: '#64748b',
        border: '2px solid #e2e8f0',
        borderRadius: 10,
        textTransform: 'none',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: '500',
        transition: 'ease-in-out 300ms',
    },
    stepContent: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 40px',
        overflowY: 'auto',
        minHeight: 0,
        ...scrollbarStyle,
    },
    stepContentFullHeight: {
        flex: 1,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        padding: 0,
        overflow: 'hidden',
        minHeight: 0,
    },
}));

const STEP_ICON_SX = { width: 24, height: 24 };

const BuildWorkflowModal: React.FC<{
    open: boolean,
    recentType?: string,
    onOpenModal?: (field?: string) => void,
    onClose: () => void,
}> = ({ open, recentType, onOpenModal, onClose }) => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState<number>(0);
    const [emailType, setEmailType] = useState<string>('New Deal');

    const steps: WorkflowStepConfig[] = useMemo(() => [
        { label: 'Email intake', sublabel: 'Forwarding address', icon: <MailOutlineRoundedIcon sx={STEP_ICON_SX} /> },
        { label: 'Email type', sublabel: 'Classification rules', icon: <LocalOfferOutlinedIcon sx={STEP_ICON_SX} /> },
        { label: 'Enrichment', sublabel: 'Fields & data sources', icon: <BoltOutlinedIcon sx={STEP_ICON_SX} /> },
        { label: 'Sync', sublabel: 'Push to your tools', icon: <SyncOutlinedIcon sx={STEP_ICON_SX} /> },
    ], []);

    const handleNext = useCallback(() => {
        setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
    }, [steps.length]);

    const handleBack = useCallback(() => {
        setActiveStep(prev => Math.max(prev - 1, 0));
    }, []);

    const handleSave = useCallback(() => {
        onClose();
    }, [onClose]);

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Stack className={classes.headerIcon} alignItems="center" justifyContent="center"
                        sx={{ 'svg > path': { fill: '#fff' } }}>
                        <WorkflowIcon style={{ width: 40, height: 40 }} />
                    </Stack>
                    <Stack direction="column" spacing={1} alignItems="flex-start">
                        <Typography fontFamily="Inter" fontSize="1.5rem" fontWeight="bold">
                            {'Build your ideal workflow'}
                        </Typography>
                        <Typography fontFamily="Lato" fontSize="1rem" fontWeight="400" color="rgba(255, 255, 255, 0.9)">
                            {'Customize how data flows from your email to your tools'}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    <IconButton className={classes.closeIcon}
                        onClick={onClose} disableRipple
                        sx={{ padding: 'unset !important' }}>
                        <CloseIcon sx={{ width: 20, height: 20, fill: 'white' }} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider sx={{ width: '100%' }} />
            <DialogContent className={classes.dialogContent}>
                <WorkflowProgressStepper steps={steps} activeStep={activeStep} onStepClick={setActiveStep} />
                <Divider sx={{ width: '100%' }} />
                <Stack className={activeStep === 2 ? classes.stepContentFullHeight : classes.stepContent}>
                    {activeStep === 0 && <NewEmail panelMode />}
                    {activeStep === 1 && <EmailType panelMode onEmailType={setEmailType} recentType={recentType} />}
                    {activeStep === 2 && <Enrichment panelMode emailType={emailType} onOpenModal={onOpenModal} />}
                    {activeStep === 3 && <Sync panelMode />}
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions className={classes.dialogActions}>
                <Button
                    className={classes.manageFolderBtn}
                    onClick={() => onOpenModal?.('Folders')}
                    startIcon={<SettingsIcon />}
                >
                    {'Manage folders'}
                </Button>
                <Stack direction="row" spacing={1.5}>
                    {activeStep > 0 && (
                        <Button
                            className={classes.backBtn}
                            onClick={handleBack}
                            startIcon={<ArrowBackIcon />}
                        >
                            {'Back'}
                        </Button>
                    )}
                    {activeStep < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            className={classes.nextBtn}
                            onClick={handleNext}
                            endIcon={<ArrowForwardIcon />}
                        >
                            {'Next'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            className={classes.nextBtn}
                            onClick={handleSave}
                        >
                            {'Save workflow'}
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    </>);
}

export default BuildWorkflowModal;
